// components/todos/TodoEditModal.jsx
import { useEffect, useMemo, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/solid";

export default function TodoEditModal({
    open,
    onClose,
    items = [],
    onSave,
    pending = false,
    error = null,
}) {
    const count = useMemo(() => Math.max(1, items.length || 5), [items]);
    const [fields, setFields] = useState(Array.from({ length: count }, () => ""));

    useEffect(() => {
        if (open) {
            // 열릴 때, 전달된 items로 채우고 부족하면 빈칸으로 채움
            const next = Array.from({ length: count }, (_, i) => items[i] ?? "");
            setFields(next);
        }
    }, [open, items, count]);

    if (!open) return null;

    const handleChange = (i, v) =>
        setFields((prev) => prev.map((x, idx) => (idx === i ? v : x)));

    const trimmed = fields.map((v) => v.trim());
    const hasAny = trimmed.some((v) => v.length > 0);
    const canSubmit = hasAny && !pending;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        await onSave?.(trimmed);
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="todo-edit-title"
            className="fixed inset-0 z-50 flex items-center justify-center"
        >
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />

            <form
                onSubmit={handleSubmit}
                className="
                relative
                w-[92vw] max-w-[720px]
                md:max-w-[550px]
                rounded-lg bg-white shadow-xl
                p-4 md:p-6
                max-h-[85vh] overflow-y-auto scrollbar-hide
                "
            >
                {/* Header */}
                <div className="relative mb-4 flex items-center justify-center">
                    <h2 id="todo-edit-title" className="text-xl md:text-2xl font-bold">
                        오늘의 할 일 수정
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="닫기"
                        className="absolute right-0 p-1 rounded-md hover:bg-gray-100 active:scale-95"
                    >
                        <XMarkIcon className="w-7 h-7 text-gray-500" />
                    </button>
                </div>

                <div className="space-y-3 max-h-[65vh] overflow-y-auto scrollbar-hide pr-1">
                    {fields.map((v, i) => (
                        <div key={i}>
                            <label className="block text-base md:text-lg mb-1">{`항목 ${i + 1}`}</label>
                            <input
                                type="text"
                                inputMode="text"
                                value={v}
                                onChange={(e) => handleChange(i, e.target.value)}
                                placeholder="내용을 입력해주세요."
                                className="
                                bg-gray-100 border border-gray-200
                                w-full rounded-lg border border-gray-200 px-3 py-2 outline-none
                                focus:ring-2 focus:ring-inset focus:ring-[#5F9BFF]
                                text-base md:text-lg
                                "
                            />
                        </div>
                    ))}
                </div>

                {error && (
                    <p className="mt-3 text-sm text-red-500">{String(error)}</p>
                )}

                <button
                    type="submit"
                    disabled={!canSubmit}
                    className={`
                        mt-4 w-full rounded-xl px-4 py-3 text-white text-base md:text-lg
                        ${canSubmit ? "bg-[#5F9BFF] hover:brightness-95 active:scale-[.99]" : "bg-[#5F9BFF]/60 cursor-not-allowed"}
                    `}
                >
                    {pending ? "저장 중..." : "변경 내용 저장"}
                </button>
            </form>
        </div>
    );
}
